import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import getUsStates, { caProvinces as getCaProvinces, IState } from '../../lib/usStates';
import Dropdown from '../Dropdown';
import styles from './USStateDropdown.module.scss';
import { isMobileDevice } from '../../lib/util/isMobileDevice';
import { ReactComponent as DropdownIndicator } from '../../assets/images/icons/chevron_icon.svg';
import classNames from 'classnames';
import LocaleContext, { FiCountry } from '../../contexts/LocaleContext';

interface USStateSelectorProps {
  state: string | undefined;
  onChange: (newState: string) => void;
  error?: boolean;
  country?: string;
}

function getStateList(country: FiCountry): IState[] {
  switch (country) {
    case FiCountry.CA:
      return getCaProvinces();
    case FiCountry.US:
    default:
      return getUsStates();
  }
}

export default function USStateSelector({ state, onChange, error, country: countryProp }: USStateSelectorProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const { country: localeCountry } = useContext(LocaleContext);
  const country = localeCountry ?? countryProp;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const stateList = getStateList(country);

  const options = useMemo(
    () =>
      stateList.map((item) => ({
        value: item.abbreviation,
        label: item.name,
      })),
    [stateList],
  );

  const selectedState = useMemo(() => {
    if (!state) {
      return;
    }

    return options.find(({ value }) => value === state);
  }, [options, state]);

  let label: string;
  switch (country) {
    case FiCountry.CA:
      label = t('province');
      break;
    case FiCountry.US:
    default:
      label = t('state');
  }

  if (isMobile) {
    return (
      <div className={styles.container}>
        <select
          id="state"
          className={classNames(styles.selectContainer, { [styles.valid]: state !== '' && !error })}
          value={state || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" disabled>
            {label}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <DropdownIndicator />
      </div>
    );
  }

  return (
    <Dropdown
      id="state"
      options={options}
      selectedOption={selectedState}
      placeholder={label}
      onChange={(option) => {
        if (option) {
          onChange(option.value);
        }
      }}
    />
  );
}
